import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Controller } from 'react-hook-form';
import { Card, MenuItem, Select, Typography } from 'seagull';
import { getAllowManualPaymentsChangeBullet, getAmendmentsApprovalMethodChangeBullet, getCoverCCFeesChangeBullet, getInvoicesApprovalMethodChangeBullet, getNetTermsChangeBullet, netTermsSelectOptions } from '~/business-logic/amendmentsLogic';
import { getAutoApprovalRange } from '~/business-logic/autoApprovalLogic';
import { approvalMethodNoticePeriodModel, relationshipModel } from '~/models';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { AllowManualPaymentsCheckbox, ApprovalMethodNoticePeriodField, CoverCreditCardFeesSelector, validateApprovalMethodNoticePeriod } from '../agreement-controllers';
import AmendmentChanges from '../AmendmentChanges/AmendmentChanges';
import styles from './EditAgreementModal.module.scss';
import { useEditAgreementForm } from './utils/EditAgreementModal.utils';
export const Section = (props)=>/*#__PURE__*/ _jsx(Card, {
        autoHeight: true,
        padding: "m",
        title: props.title,
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.sectionContent,
            children: props.children
        })
    });
export const SettingsField = (props)=>/*#__PURE__*/ _jsx("div", {
        className: styles.settingsField,
        children: props.children
    });
export function EditNetTerms(props) {
    const { relationship } = props;
    const { watch, control } = useEditAgreementForm();
    const updatedNetTerms = watch('updatedNetTerms');
    const netTermsChangeBullet = getNetTermsChangeBullet(relationship, updatedNetTerms);
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "updatedNetTerms",
        render: (p)=>{
            const _connectControllerToTextField = connectControllerToTextField(p), { onChange } = _connectControllerToTextField, rest = _object_without_properties(_connectControllerToTextField, [
                "onChange"
            ]);
            return /*#__PURE__*/ _jsxs(SettingsField, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        children: "Set the number of days the payment is due after the invoice date"
                    }),
                    /*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                        collapseHelperText: false,
                        menuMaxHeight: 200,
                        width: 280,
                        testId: "edit-net-terms",
                        onChange: (e)=>onChange(+e.target.value)
                    }, rest), {
                        children: netTermsSelectOptions.map((param)=>{
                            let { value, text } = param;
                            return /*#__PURE__*/ _jsx(MenuItem, {
                                value: value.toString(),
                                testId: "net-terms-opt-".concat(value),
                                children: text
                            }, value);
                        })
                    })),
                    netTermsChangeBullet && /*#__PURE__*/ _jsx(AmendmentChanges, {
                        disableNote: true,
                        disableBorderRadius: true,
                        items: [
                            netTermsChangeBullet
                        ],
                        isUnsaved: p.fieldState.isDirty,
                        testId: "net-terms-changes",
                        autoApprovalDate: relationshipModel.updatedNetTermsAutoApprovalDate(relationship),
                        onDiscard: ()=>onChange(relationship.netTerms)
                    })
                ]
            });
        }
    });
}
export function EditCCFees(props) {
    const { relationship } = props;
    const { control, watch } = useEditAgreementForm();
    const coverCreditCardFees = watch('coverCreditCardFees');
    const changeBullet = getCoverCCFeesChangeBullet(relationship, coverCreditCardFees);
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "coverCreditCardFees",
        render: (param)=>{
            let { field: { value, onChange }, fieldState: { isDirty } } = param;
            return /*#__PURE__*/ _jsxs(SettingsField, {
                children: [
                    /*#__PURE__*/ _jsx(CoverCreditCardFeesSelector, {
                        testId: "cc-fees",
                        vendorBusinessName: relationshipModel.vendorName(relationship),
                        clientBusinessName: relationshipModel.customerName(relationship),
                        value: value,
                        onChange: onChange
                    }),
                    changeBullet && /*#__PURE__*/ _jsx(AmendmentChanges, {
                        disableNote: true,
                        disableBorderRadius: true,
                        items: [
                            changeBullet
                        ],
                        isUnsaved: isDirty,
                        testId: "cc-fees-changes",
                        autoApprovalDate: relationshipModel.updatedIsVendorCoveringCCFeeAutoApprovalDate(relationship),
                        onDiscard: ()=>onChange(relationshipModel.isVendorCoveringCCFee(relationship))
                    })
                ]
            });
        }
    });
}
const EditApprovalMethodNoticePeriod = (param)=>{
    let { info, text, testId, fieldName, relationship, autoApprovalDate, getApprovalMethodChangeBullet } = param;
    const { control, watch } = useEditAgreementForm();
    const approvalMethod = watch(fieldName);
    const noticePeriodDays = approvalMethodNoticePeriodModel.noticePeriodDays(approvalMethod);
    const changeBullet = getApprovalMethodChangeBullet(relationship[fieldName], approvalMethod);
    return /*#__PURE__*/ _jsx(Controller, {
        name: fieldName,
        control: control,
        rules: {
            validate: (value)=>validateApprovalMethodNoticePeriod(approvalMethodNoticePeriodModel.isAutoApproval(value), approvalMethodNoticePeriodModel.noticePeriodDays(value) || '')
        },
        render: (param)=>{
            let { field: { value, onChange }, fieldState: { isDirty } } = param;
            return /*#__PURE__*/ _jsxs(SettingsField, {
                children: [
                    /*#__PURE__*/ _jsx(ApprovalMethodNoticePeriodField, {
                        testId: testId,
                        info: info,
                        text: text,
                        enabled: approvalMethodNoticePeriodModel.isAutoApproval(value),
                        noticePeriod: noticePeriodDays != null ? noticePeriodDays : '',
                        onToggle: (enabled)=>{
                            if (enabled) {
                                const { maxDays } = getAutoApprovalRange();
                                onChange(approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod(maxDays));
                            } else {
                                onChange(approvalMethodNoticePeriodModel.makeManualApprovalMethodNoticePeriod());
                            }
                        },
                        onChangePeriod: (noticePeriod)=>{
                            if (noticePeriod === '') return;
                            onChange(approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod(noticePeriod));
                        }
                    }),
                    changeBullet && /*#__PURE__*/ _jsx(AmendmentChanges, {
                        disableNote: true,
                        disableBorderRadius: true,
                        items: [
                            changeBullet
                        ],
                        isUnsaved: isDirty,
                        testId: "".concat(testId, "-changes"),
                        autoApprovalDate: autoApprovalDate,
                        onDiscard: ()=>onChange(relationship[fieldName])
                    })
                ]
            });
        }
    });
};
export const EditAmendmentsApprovalMethod = (param)=>{
    let { relationship } = param;
    return /*#__PURE__*/ _jsx(EditApprovalMethodNoticePeriod, {
        info: "Amendments will take effect automatically if the client doesn't disable within the notice period set. Amended invoices issued during the notice period will require client approval.",
        text: 'Automatically approve amendments after a defined period',
        testId: 'amendments-approval-method',
        fieldName: 'amendmentsApprovalMethod',
        relationship: relationship,
        autoApprovalDate: relationshipModel.updatedAmendmentsApprovalMethodAutoApprovalDate(relationship),
        getApprovalMethodChangeBullet: getAmendmentsApprovalMethodChangeBullet
    });
};
export const EditInvoicesApprovalMethod = (param)=>{
    let { relationship } = param;
    return /*#__PURE__*/ _jsx(EditApprovalMethodNoticePeriod, {
        info: "Invoices that require client approval will be approved automatically if the client doesn't disable within the notice period set.",
        text: "Automatically approve out-of-scope charges after a defined period",
        testId: 'invoices-approval-method',
        fieldName: 'invoicesApprovalMethod',
        relationship: relationship,
        autoApprovalDate: relationshipModel.updatedInvoicesApprovalMethodAutoApprovalDate(relationship),
        getApprovalMethodChangeBullet: getInvoicesApprovalMethodChangeBullet
    });
};
export function EditAllowManualPayments(props) {
    const { relationship } = props;
    const { control, watch } = useEditAgreementForm();
    const allowManualPayments = watch('allowManualPayments');
    const changeBullet = getAllowManualPaymentsChangeBullet(relationship, allowManualPayments);
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "allowManualPayments",
        render: (param)=>{
            let { field: { value, onChange }, fieldState: { isDirty } } = param;
            return /*#__PURE__*/ _jsxs(SettingsField, {
                children: [
                    /*#__PURE__*/ _jsx(AllowManualPaymentsCheckbox, {
                        value: value,
                        onChange: (newValue)=>onChange(newValue)
                    }),
                    changeBullet && /*#__PURE__*/ _jsx(AmendmentChanges, {
                        disableNote: true,
                        disableBorderRadius: true,
                        items: [
                            changeBullet
                        ],
                        isUnsaved: isDirty,
                        testId: "allow-manual-payments-changes",
                        autoApprovalDate: relationshipModel.updatedAllowManualPaymentsAutoApprovalDate(relationship),
                        onDiscard: ()=>onChange(relationship.allowManualPayments)
                    })
                ]
            });
        }
    });
}
